<template>
  <f7-page
    name="order-box-totals"
    :page-content="false"
    @page:init="onPageInit"
    @page:beforeremove="onPageBeforeRemove"
  >
    <!-- `back-link-show-text` is required for the «رجوع» label to render at
         all: F7's nav-left drops the label unless it is set, leaving a bare
         chevron (nav-left.js — `backLink !== true && backLinkShowText`). -->
    <f7-navbar :back-link="$t('general.back')" back-link-show-text>
      <f7-nav-title>{{ navTitle }}</f7-nav-title>
    </f7-navbar>

    <!-- DELIVERED-only sticky CTA. MUST live as a direct child of <f7-page>,
         not inside <f7-page-content>; F7's .toolbar-bottom is position: fixed
         relative to .page and is only sorted into the fixed slot when it's a
         top-level page child.

         Rendered in BOTH loading and loaded states so the bottom CTA space is
         reserved during skeleton render — avoids a 64px layout jolt when the
         data lands. The button is disabled (and reads as a skeleton) while
         loading. -->
    <f7-toolbar
      v-if="loading || (!loadError && needsMemberDelivery)"
      bottom
      class="totals-cta-toolbar"
    >
      <f7-button
        fill
        large
        color="green"
        :disabled="loading || marking"
        class="totals-cta"
        :class="{ 'totals-cta-skeleton': loading }"
        @click="askMarkDelivered"
      >
        <template v-if="loading">
          <span class="skeleton-text skeleton-effect-blink">&nbsp;</span>
        </template>
        <template v-else>
          <f7-preloader
            v-if="marking"
            size="20"
            class="margin-right-half"
          ></f7-preloader>
          <f7-icon
            v-else
            f7="checkmark_circle_fill"
            class="margin-right-half"
          ></f7-icon>
          {{ $t('box.totals.markDeliveredCta') }}
        </template>
      </f7-button>
    </f7-toolbar>

    <f7-page-content>
      <!-- Loading -->
      <div v-if="loading" class="totals-skeleton">
        <f7-block strong-ios outline-ios inset>
          <div class="skeleton-text skeleton-effect-blink skel-w60 skel-h18" />
          <div class="skeleton-text skeleton-effect-blink skel-w40 skel-h14 margin-top-half" />
        </f7-block>
        <f7-block strong-ios outline-ios inset>
          <div class="skeleton-text skeleton-effect-blink skel-w100 skel-h80" />
        </f7-block>
        <f7-block strong-ios outline-ios inset>
          <div class="skeleton-text skeleton-effect-blink skel-w100 skel-h160" />
        </f7-block>
      </div>

      <!-- Error -->
      <f7-block
        v-else-if="loadError"
        strong-ios
        outline-ios
        inset
        class="totals-error"
      >
        <div class="totals-error-content">
          <f7-icon f7="exclamationmark_triangle_fill" size="32" color="red"></f7-icon>
          <p>{{ loadError }}</p>
          <f7-button outline @click="loadData">
            {{ $t('common.errorTryAgain') }}
          </f7-button>
        </div>
      </f7-block>

      <!-- Loaded -->
      <template v-else-if="total">
        <!-- Stage banner. On a settled carton with its account the card's own
             header already says «مدفوعة · اتأكد الاستلام …», so the banner would
             only repeat it; shipped/confirm keep it for the stage they are in. -->
        <f7-block
          v-if="showStageBanner"
          strong-ios
          inset
          class="totals-banner"
          :class="`totals-banner-${variant}`"
        >
          <div class="totals-banner-inner">
            <f7-icon
              :f7="bannerIcon"
              size="28"
              :color="bannerColor"
            ></f7-icon>
            <div class="totals-banner-text">
              <div class="totals-banner-title">{{ bannerTitle }}</div>
              <div v-if="bannerSubtitle" class="totals-banner-subtitle">{{ bannerSubtitle }}</div>
            </div>
          </div>
        </f7-block>

        <!-- "Tap to refresh" chip — surfaces when a background poll detected a
             newer invoice version. We do NOT swap underneath the user — they
             opt in. -->
        <f7-block v-if="pendingUpdate" inset class="totals-update-chip-wrapper">
          <f7-button
            small
            round
            fill
            color="orange"
            class="totals-update-chip"
            @click="applyPendingUpdate"
          >
            <f7-icon f7="arrow_2_circlepath" size="14" class="margin-right-half"></f7-icon>
            {{ $t('box.totals.updateAvailable') }}
          </f7-button>
        </f7-block>

        <!-- The carton banner-link (tips-wave P3-A, owner answer W-K): the old
             totals page is still where the out-of-stock pushes and the 1,155
             Telegram messages already sitting in members' chats point, so a
             single tap from here reaches the new carton route where the item
             sheet, cancel and «أنا عارف مين عنده» actually live. -->
        <f7-block v-if="showOpenCartonBanner" inset class="totals-open-carton-wrapper">
          <f7-button
            large
            outline
            class="totals-open-carton"
            @click="openCartonDetail"
          >
            <f7-icon f7="cube_box_fill" size="18" class="margin-end-half"></f7-icon>
            {{ t('box.totals.openCarton', { count: outOfStockCount }) }}
          </f7-button>
        </f7-block>

        <!-- Block ordering varies by variant:
             - draft (DRAFT/ACTIVE/READY/PACKED): totals first, no shipping yet.
             - shipped (SHIPPED): shipping leads ("where's my stuff?"), totals as reference.
             - confirm (DELIVERED) / receipt (CLOSED): totals lead ("what am I paying / paid"),
               shipping as reference. -->

        <template v-if="variant === 'shipped'">
          <f7-block-header v-if="hasShipping">{{ $t('box.shipping.info') }}</f7-block-header>
          <f7-list
            v-if="hasShipping"
            strong-ios
            outline-ios
            dividers-ios
            inset
            media-list
          >
            <f7-list-item :title="$t('box.shipping.name')" :after="box.shipName">
              <template #media>
                <f7-icon f7="person_fill" size="22" color="purple"></f7-icon>
              </template>
            </f7-list-item>
            <f7-list-item :title="$t('box.shipping.phone')" :after="box.shipPhone">
              <template #media>
                <f7-icon f7="phone_fill" size="22" color="purple"></f7-icon>
              </template>
            </f7-list-item>
            <f7-list-item :title="$t('box.shipping.address')">
              <template #media>
                <f7-icon f7="house_fill" size="22" color="purple"></f7-icon>
              </template>
              <template #after>
                <div class="totals-address">{{ box.shipAddress }}</div>
              </template>
            </f7-list-item>
          </f7-list>

          <f7-block-header>{{ $t('box.totals.breakdown') }}</f7-block-header>
          <f7-block inset class="totals-breakdown-wrapper">
            <!-- The «دفعتِ» headline is the true number and stays at the top:
                 a pre-paid Poola carton's invoice total is 0, so swapping in
                 <box-totals-breakdown> would open a receipt for goods she paid
                 1,854.00 for and lead with a zero. Once the carton carries an
                 invoiceNumber the card also renders the rest of the receipt —
                 the per-line saving, the 5% commission, the waiver that
                 cancels it, and what is actually due now. The invoice columns
                 are read off the box, NOT off `total`: see the model note on
                 OrderBox.commissionCents. -->
            <poola-paid-card
              v-if="isPoolaBox"
              :amount-cents="poolaPaidCents"
              :item-count="box?.totalItems ?? 0"
              :invoice-number="box?.invoiceNumber ?? null"
              :items="items"
              :commission-cents="box?.commissionCents ?? 0"
              :commission-rate-bp="box?.commissionRateBp ?? 0"
              :adjustments="adjustmentsJson"
              :due-cents="box?.totalInvoiceCents ?? 0"
            ></poola-paid-card>
            <!-- The carton account (brief 2026-09-29): once the carton ships the
                 backend sends the PDF's own numbers, so the page renders those
                 instead of re-deriving a breakdown. Null → today's view. -->
            <carton-account-card
              v-else-if="cartonAccount"
              :account="cartonAccount"
              :fallback-name="box?.title ?? ''"
              :box-items="allBoxItems"
              :downloading="downloadingPdf"
              @top-up="openTopUp"
              @open-transaction="openTransaction"
              @download-pdf="downloadPdf"
            ></carton-account-card>
            <box-totals-breakdown
              v-else
              :total="total"
              :items="items"
              :adjustments-json="adjustmentsJson"
              :potential-pool-share-cents="potentialPoolShareCents"
            ></box-totals-breakdown>
          </f7-block>

          <template v-if="box?.invoiceNumber && !cartonAccount">
            <f7-block-header>{{ $t('box.totals.invoiceNumber') }}</f7-block-header>
            <f7-block strong-ios outline-ios inset class="totals-meta">
              <div class="totals-meta-row">
                <span class="totals-meta-label">#</span>
                <span class="totals-meta-value">{{ box.invoiceNumber }}</span>
              </div>
              <div v-if="box.shippedAt" class="totals-meta-row">
                <span class="totals-meta-label">{{ $t('box.totals.shippedOn') }}</span>
                <span class="totals-meta-value">{{ formatTs(box.shippedAt) }}</span>
              </div>
            </f7-block>
          </template>
        </template>

        <template v-else>
          <f7-block-header>{{ $t('box.totals.breakdown') }}</f7-block-header>
          <f7-block inset class="totals-breakdown-wrapper">
            <poola-paid-card
              v-if="isPoolaBox"
              :amount-cents="poolaPaidCents"
              :item-count="box?.totalItems ?? 0"
              :invoice-number="box?.invoiceNumber ?? null"
              :items="items"
              :commission-cents="box?.commissionCents ?? 0"
              :commission-rate-bp="box?.commissionRateBp ?? 0"
              :adjustments="adjustmentsJson"
              :due-cents="box?.totalInvoiceCents ?? 0"
            ></poola-paid-card>
            <!-- The carton account (brief 2026-09-29): once the carton ships the
                 backend sends the PDF's own numbers, so the page renders those
                 instead of re-deriving a breakdown. Null → today's view. -->
            <carton-account-card
              v-else-if="cartonAccount"
              :account="cartonAccount"
              :fallback-name="box?.title ?? ''"
              :box-items="allBoxItems"
              :downloading="downloadingPdf"
              @top-up="openTopUp"
              @open-transaction="openTransaction"
              @download-pdf="downloadPdf"
            ></carton-account-card>
            <box-totals-breakdown
              v-else
              :total="total"
              :items="items"
              :adjustments-json="adjustmentsJson"
              :potential-pool-share-cents="potentialPoolShareCents"
            ></box-totals-breakdown>
          </f7-block>

          <template v-if="box?.invoiceNumber && !cartonAccount">
            <f7-block-header>{{ $t('box.totals.invoiceNumber') }}</f7-block-header>
            <f7-block strong-ios outline-ios inset class="totals-meta">
              <div class="totals-meta-row">
                <span class="totals-meta-label">#</span>
                <span class="totals-meta-value">{{ box.invoiceNumber }}</span>
              </div>
              <div v-if="box.shippedAt" class="totals-meta-row">
                <span class="totals-meta-label">{{ $t('box.totals.shippedOn') }}</span>
                <span class="totals-meta-value">{{ formatTs(box.shippedAt) }}</span>
              </div>
            </f7-block>
          </template>

          <template v-if="hasShipping">
            <f7-block-header>{{ $t('box.shipping.info') }}</f7-block-header>
            <f7-list
              strong-ios
              outline-ios
              dividers-ios
              inset
              media-list
            >
              <f7-list-item :title="$t('box.shipping.name')" :after="box.shipName">
                <template #media>
                  <f7-icon f7="person_fill" size="22" color="purple"></f7-icon>
                </template>
              </f7-list-item>
              <f7-list-item :title="$t('box.shipping.phone')" :after="box.shipPhone">
                <template #media>
                  <f7-icon f7="phone_fill" size="22" color="purple"></f7-icon>
                </template>
              </f7-list-item>
              <f7-list-item :title="$t('box.shipping.address')">
                <template #media>
                  <f7-icon f7="house_fill" size="22" color="purple"></f7-icon>
                </template>
                <template #after>
                  <div class="totals-address">{{ box.shipAddress }}</div>
                </template>
              </f7-list-item>
            </f7-list>
          </template>
        </template>

        <!-- Settled-only confirmation metadata -->
        <template v-if="variant === 'receipt' && box?.deliveredConfirmedAt && !cartonAccount">
          <f7-block-header>{{ $t('box.totals.confirmation') }}</f7-block-header>
          <f7-list strong-ios outline-ios dividers-ios inset>
            <f7-list-item
              :title="$t('box.totals.confirmedAt')"
              :after="formatTs(box.deliveredConfirmedAt)"
            >
              <template #media>
                <f7-icon f7="checkmark_seal_fill" size="22" color="green"></f7-icon>
              </template>
            </f7-list-item>
            <f7-list-item
              :title="$t('box.totals.confirmedBy')"
              :after="$t(`box.totals.confirmedByWho.${confirmedByKey}`)"
            >
              <template #media>
                <f7-icon f7="person_crop_circle_fill" size="22" color="green"></f7-icon>
              </template>
            </f7-list-item>
          </f7-list>
        </template>

      </template>
    </f7-page-content>

    <!-- A carton-account wallet row opens the wallet's own detail sheet — one
         sheet, one set of words for a row wherever he meets it. -->
    <transaction-detail-sheet
      v-model:opened="detailSheetOpened"
      :transaction="detailTransaction"
      :current-order-box-id="boxId"
    ></transaction-detail-sheet>
  </f7-page>
</template>

<script setup>
import { echo } from '@laravel/echo-vue';
import { f7 } from 'framework7-vue';
import { useRepo } from 'pinia-orm';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BoxTotalsBreakdown from '@/components/orders/BoxTotalsBreakdown.vue';
import CartonAccountCard from '@/components/orders/CartonAccountCard.vue';
import PoolaPaidCard from '@/components/orders/PoolaPaidCard.vue';
import TransactionDetailSheet from '@/components/wallet/TransactionDetailSheet.vue';
import { useAuth } from '@/composables/useAuth.js';
import { useCurrency } from '@/composables/useCurrency.js';
import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
import { OrderBoxStatus, OrderBoxStatusMeta } from '@/enums/commerce/OrderBoxStatus.enum.js';
import OrderBox from '@/models/commerce/OrderBox.js';
import OrderBoxItem from '@/models/commerce/OrderBoxItem.js';
import { adapter } from '@/platform/index.js';
import { onReconnected } from '@/realtime/echo.js';
import {
  fetchInvoice,
  fetchInvoicePdfUrl,
  INVOICE_EMPTY_BODY,
  INVOICE_FORBIDDEN,
  INVOICE_INVALID_STATE,
  markDelivered,
} from '@/services/orderBoxInvoice.service.js';
import { transactionsService } from '@/services/wallet/transactions.service.js';
import { formatDateTime } from '@/utils/orderUtils.js';

const props = defineProps({
  f7route: { type: Object, required: true },
  f7router: { type: Object, required: true },
});

const { t, locale } = useI18n();
const { formatCurrency } = useCurrency();
const { membership } = useAuth();
const boxId = computed(() => props.f7route?.params?.boxId);

const box = ref(null);
const loading = ref(true);
const loadError = ref('');
const marking = ref(false);
// A polled refetch can return a newer invoice version while the member is
// reading the current one. We surface a "tap to refresh" chip instead of
// silently swapping — mid-action UI churn is bad while the member is deciding.
const pendingUpdate = ref(null);

const total = computed(() => box.value?.estimatedTotal ?? null);

// A dedicated Poola carton is pre-paid and non-billable, so its invoice/estimate
// total is 0. Show the net cash actually paid (the box's net total_amount) instead
// of the invoice breakdown.
const isPoolaBox = computed(() => Boolean(box.value?.isPoola));
const poolaPaidCents = computed(() => box.value?.totalAmount ?? 0);

const adjustmentsJson = computed(() => box.value?.adjustmentsJson ?? []);

// The carton account (brief 2026-09-29-carton-account) rides on the /invoice
// response, so it is only ever present from SHIPPED on. A Poola carton keeps its
// PoolaPaidCard (the July ones get null anyway), and the draft variant keeps the
// estimate card — a DRAFT box has no account to show.
const cartonAccount = computed(() => {
  if (isPoolaBox.value || variant.value === 'draft' || variant.value === 'unknown') return null;
  return box.value?.cartonAccount ?? null;
});

const showStageBanner = computed(() => !(variant.value === 'receipt' && cartonAccount.value));

// «اشحن المحفظة» under the due ask — the wallet route opens its deposit sheet.
function openTopUp() {
  props.f7router?.navigate('/wallet/?autoOpenDeposit=1');
}

// A carton-account row carries only its `transactionId`; fetch the ledger row by id
// so the sheet gets exactly what the wallet page would hand it.
const detailSheetOpened = ref(false);
const detailTransaction = ref(null);
let openingTransactionId = null;

async function openTransaction(transactionId) {
  if (transactionId == null || openingTransactionId != null) return;
  openingTransactionId = transactionId;
  try {
    const res = await transactionsService.show(transactionId);
    const row = res?.data?.data;
    if (!row) throw new Error('empty transaction');
    detailTransaction.value = row;
    detailSheetOpened.value = true;
  } catch (err) {
    console.warn('[OrderBoxTotalsPage] transaction fetch failed', err);
    f7?.toast?.create?.({
      text: t('box.totals.account.txFailed'),
      position: 'center',
      closeTimeout: 2000,
    })?.open();
  } finally {
    openingTransactionId = null;
  }
}

watch(detailSheetOpened, (opened) => {
  // Keep the row through the close animation, then drop it (as WalletPage does).
  if (!opened) setTimeout(() => { if (!detailSheetOpened.value) detailTransaction.value = null; }, 300);
});

// «تحميل الفاتورة PDF» — the backend regenerates a stale file on this read and
// answers a short-lived URL; the platform adapter opens it (Telegram's openLink
// inside the Mini App, a new tab in the PWA).
const downloadingPdf = ref(false);

async function downloadPdf() {
  if (downloadingPdf.value || boxId.value == null) return;
  downloadingPdf.value = true;
  try {
    const url = await fetchInvoicePdfUrl(boxId.value);
    adapter.openExternalLink(url);
  } catch (err) {
    console.warn('[OrderBoxTotalsPage] invoice PDF failed', err);
    f7?.toast?.create?.({
      text: t('box.totals.account.pdfFailed'),
      position: 'center',
      closeTimeout: 2200,
    })?.open();
  } finally {
    downloadingPdf.value = false;
  }
}

// Pre-DELIVERED: aggregate of *potential* poolshare yield across in-flight
// items — the carrot for tapping "Confirm delivery".
// Post-DELIVERED/CLOSED: equals the amount actually released into the
// PoolShare wallet (backend keeps the same field hot through settlement so
// the receipt can show "PoolShare earned: X").
const potentialPoolShareCents = computed(() => box.value?.potentialPoolShareCents ?? 0);

// The /invoice endpoint's items relation is filtered server-side to
// SHIPPED + DELIVERED items only — items in PENDING/OUT_OF_STOCK/CANCELLED/
// WRITTEN_OFF/collection-phase aren't on the bill. The store still holds
// the unfiltered set from /order-boxes/{id} (and prior list fetches), so we
// filter here to match the invoice view. The 'draft' variant uses the live
// box endpoint and intentionally shows everything (collection in progress).
const INVOICE_ITEM_STATUSES = [ItemStatus.SHIPPED, ItemStatus.DELIVERED];

const items = computed(() => {
  if (!box.value) return [];
  const all = useRepo(OrderBoxItem).where('orderBoxId', box.value.id).get();
  if (variant.value === 'draft' || variant.value === 'unknown') return all;
  return all.filter(i => INVOICE_ITEM_STATUSES.includes(i.status));
});

// The banner-link to the carton route (tips-wave P3-A, owner answer W-K). Read
// off the box's WHOLE line set — not `items` above, which the shipped/confirm/
// receipt variants already filter down to SHIPPED+DELIVERED only — because an
// out-of-stock line can sit on a carton in any of those stages (the "ask to
// ship without it" edge, brief 2026-09-15-ask-to-ship-notice-and-member-reopen)
// and must still surface here even though it never appears in the invoice list.
const allBoxItems = computed(() => {
  if (!box.value) return [];
  return useRepo(OrderBoxItem).where('orderBoxId', box.value.id).get();
});

const outOfStockCount = computed(() => allBoxItems.value.filter((item) => {
  const raw = item?.status;
  const status = typeof raw === 'string' ? Number(raw) : raw;
  return status === ItemStatus.OUT_OF_STOCK;
}).length);

// ONE banner-link (W-K): the 1,155 out-of-stock messages already sitting in
// members' Telegram, and any push whose deep link still carries the old
// `.../totals/` URL, reach the carton's actions in one tap from here. Keyed
// on the count alone — a READY carton with nothing at 41 has nothing this
// banner would be naming, so it stays quiet rather than saying "0 items".
const showOpenCartonBanner = computed(() => outOfStockCount.value > 0);

function openCartonDetail() {
  if (boxId.value == null) return;
  props.f7router?.navigate(`/orders/box/${boxId.value}/`);
}

const hasShipping = computed(() => Boolean(
  box.value?.shipName?.trim() || box.value?.shipAddress?.trim(),
));

// Variant order:
//   draft   — pre-SHIP, estimate disclaimer visible
//   shipped — totals locked, in transit
//   confirm — DELIVERED, waiting on member
//   receipt — settled (CLOSED + isFinal)
const variant = computed(() => {
  if (!total.value) return 'unknown';
  if (total.value.isFinal) return 'receipt';
  if (box.value?.status === OrderBoxStatus.DELIVERED) return 'confirm';
  if (total.value.stage === 'shipped') return 'shipped';
  return 'draft';
});

// The member's actionable moment is now the SHIPPED state — they mark the box
// delivered ("I received it"). The DELIVERED ('confirm') state is passive: the
// member has acknowledged receipt and is waiting on the admin to settle.
const needsMemberDelivery = computed(() => variant.value === 'shipped');

const navTitle = computed(() => {
  // One name everywhere (L-A): with its account the carton is «كرتونة #N».
  if (cartonAccount.value?.header?.title) return cartonAccount.value.header.title;
  if (variant.value === 'confirm') return t('box.totals.title.confirm');
  if (variant.value === 'receipt') return t('box.totals.title.receipt');
  if (variant.value === 'shipped') return t('box.totals.title.shipped');
  return t('box.totals.title.draft');
});

const bannerTitle = computed(() => {
  if (variant.value === 'receipt') return t('box.totals.banner.closed');
  if (variant.value === 'confirm') return t('box.totals.banner.delivered');
  if (variant.value === 'shipped') return t('box.totals.banner.shipped');
  return t('box.totals.banner.draft');
});

const bannerSubtitle = computed(() => {
  if (variant.value === 'receipt' && box.value?.deliveredConfirmedAt) {
    return `${t('box.totals.settledOn')} · ${formatTs(box.value.deliveredConfirmedAt)}`;
  }
  if (variant.value === 'confirm') {
    // Reframe the confirm step from "obligation" to "value moment" by naming
    // the unlock amount inline. Falls back to the plain subtitle when the box
    // has no poolshare yield (e.g. all items ineligible).
    return potentialPoolShareCents.value > 0
      ? t('box.totals.banner.deliveredSubtitleWithShare', { amount: formatCurrency(potentialPoolShareCents.value) })
      : t('box.totals.banner.deliveredSubtitle');
  }
  if (variant.value === 'shipped' && box.value?.shippedAt) {
    return `${t('box.totals.shippedOn')} · ${formatTs(box.value.shippedAt)}`;
  }
  if (variant.value === 'draft') return t('box.totals.banner.draftSubtitle');
  return '';
});

const bannerIcon = computed(() => {
  if (variant.value === 'draft') return 'pencil_circle_fill';
  const meta = OrderBoxStatusMeta[box.value?.status];
  return meta?.icon ?? 'cube_box';
});

const bannerColor = computed(() => {
  if (variant.value === 'draft') return 'orange';
  const meta = OrderBoxStatusMeta[box.value?.status];
  return meta?.color ?? 'gray';
});

const confirmedByKey = computed(() => {
  const m = box.value?.deliveredConfirmationMethod;
  if (m === 'admin') return 'admin';
  if (m === 'auto') return 'auto';
  return 'member';
});

const formatTs = (ts) => {
  if (!ts) return '';
  const parts = formatDateTime(ts, locale.value);
  return parts ? `${parts.date} · ${parts.time}` : '';
};

function readBoxFromStore() {
  const id = boxId.value;
  if (!id) return null;
  return OrderBox.withItems(id);
}

/**
 * Hydrate the page. Strategy:
 *  1) Always fetch the single box via JSON:API (cheap, returns estimatedTotal +
 *     items in one round trip). Source-of-truth for the breakdown.
 *  2) If the status is SHIPPED+, additionally fetch the invoice for the
 *     invoiceNumber / invoiceVersion / shippedAt / confirmation metadata that
 *     the standard resource doesn't carry.
 *
 * The `silent` mode is used by the poll + visibility refetch — it must NOT
 * mutate `loading` / `loadError` on failure, otherwise a transient network
 * drop would replace the user's already-rendered confirm screen with a
 * full-page error block and steal the CTA they were about to tap.
 */
async function loadData({ silent = false } = {}) {
  if (!silent) {
    loading.value = true;
    loadError.value = '';
  }

  try {
    await OrderBox.fetchOne(boxId.value);
  } catch (err) {
    if (silent) {
      console.warn('[OrderBoxTotalsPage] silent box refetch failed', err);
      return;
    }
    if (err?.response?.status === 404) {
      f7?.toast?.create?.({
        text: t('box.totals.notFound'),
        position: 'center',
        closeTimeout: 2000,
      })?.open();
      props.f7router.back();
      return;
    }
    console.error('[OrderBoxTotalsPage] fetchOne failed', err);
    loadError.value = err?.response?.data?.errors?.[0]?.detail
      || err?.message
      || t('common.errorTryAgain');
    return;
  } finally {
    if (!silent) loading.value = false;
  }

  const fresh = readBoxFromStore();
  if (!fresh) {
    // Defensive — Pinia-ORM should have populated this. If not, treat as
    // load failure so the user gets a retry option.
    if (!silent) loadError.value = t('box.totals.notFound');
    return;
  }

  // Silent + post-SHIP version-change detection. Done BEFORE we apply the
  // fresh box so we can detect the version delta against the in-memory copy.
  const prevVersion = box.value?.invoiceVersion ?? null;
  const prevStatus = box.value?.status ?? null;

  // If the box is SHIPPED+ we also need invoice-document metadata (invoiceNumber,
  // invoiceVersion, shippedAt, deliveredConfirmedAt…) — fetch on top.
  if (fresh.status >= OrderBoxStatus.SHIPPED) {
    let invoice;
    try {
      invoice = await fetchInvoice(boxId.value);
      useRepo(OrderBox).save(invoice);
    } catch (err) {
      if (silent) {
        console.warn('[OrderBoxTotalsPage] silent invoice refetch failed', err);
      } else if (err?.code === INVOICE_FORBIDDEN || err?.status === 403) {
        f7?.toast?.create?.({
          text: t('box.totals.forbidden'),
          position: 'center',
          closeTimeout: 2000,
        })?.open();
        props.f7router.back();
        return;
      } else {
        // The estimatedTotal alone is still renderable — log and continue
        // rather than fail the whole page when the invoice endpoint is
        // misbehaving. The confirm CTA path will surface its own error.
        console.error('[OrderBoxTotalsPage] fetchInvoice failed', err);
      }
    }
  }

  // For silent (poll/visibility) refetches: if the server returned a newer
  // invoice version, stash it as pendingUpdate and let the user opt in via
  // the chip. The exception is a status transition (DELIVERED → CLOSED) —
  // apply immediately because the variant must re-render to hide the CTA
  // and avoid the user tapping a stale Confirm button.
  const next = readBoxFromStore();
  if (silent && box.value) {
    const statusChanged = next?.status !== prevStatus;
    const versionChanged = next?.invoiceVersion !== prevVersion
      && next?.invoiceVersion != null
      && prevVersion != null;
    if (statusChanged) {
      box.value = next;
    } else if (versionChanged) {
      pendingUpdate.value = next;
      return; // don't touch box.value — user is mid-read
    } else {
      box.value = next;
    }
  } else {
    box.value = next;
    pendingUpdate.value = null;
  }
}

function applyPendingUpdate() {
  if (!pendingUpdate.value) return;
  box.value = pendingUpdate.value;
  pendingUpdate.value = null;
  f7?.toast?.create?.({
    text: t('box.totals.updated'),
    position: 'center',
    closeTimeout: 1800,
  })?.open();
}

function askMarkDelivered() {
  // A light receipt acknowledgement — NOT a money moment. Settlement is a
  // separate, admin-only step, so there is deliberately no total / irreversible
  // wallet warning here; just a guard against an accidental tap.
  f7?.dialog?.create?.({
    title: t('box.totals.markDeliveredCta'),
    text: t('box.totals.markDeliveredPrompt'),
    buttons: [
      { text: t('general.cancel'), close: true },
      {
        text: t('box.totals.markDeliveredConfirm'),
        bold: true,
        color: 'green',
        close: true,
        onClick: () => { runMarkDelivered(); },
      },
    ],
  })?.open();
}

async function runMarkDelivered() {
  marking.value = true;
  try {
    const updated = await markDelivered(boxId.value);
    try {
      useRepo(OrderBox).save(updated);
    } catch (persistErr) {
      console.error('[OrderBoxTotalsPage] OrderBox persistence failed', persistErr);
    }
    box.value = readBoxFromStore();
    f7?.toast?.create?.({
      text: t('box.totals.markedDelivered'),
      position: 'center',
      closeTimeout: 2600,
      cssClass: 'toast-success',
    })?.open();
    return;
  } catch (err) {
    // 422 → the box already left SHIPPED (an admin ship-bypass, or the
    // auto-deliver cron beat the member). Refetch so the page re-renders as
    // the DELIVERED / awaiting-admin state, then explain rather than offer retry.
    if (err?.code === INVOICE_INVALID_STATE) {
      await loadData({ silent: true });
      f7?.toast?.create?.({
        text: t('box.totals.alreadyDelivered'),
        position: 'center',
        closeTimeout: 2400,
      })?.open();
      return;
    }
    if (err?.code === INVOICE_FORBIDDEN) {
      f7?.toast?.create?.({
        text: t('box.totals.forbidden'),
        position: 'center',
        closeTimeout: 2000,
      })?.open();
      return;
    }
    // 2xx with an empty body — the mark may have landed server-side. Refetch
    // and ask the member to verify rather than risk a confusing "failed"
    // message (a retry against an already-DELIVERED box would 422 anyway).
    if (err?.code === INVOICE_EMPTY_BODY) {
      console.error('[OrderBoxTotalsPage] markDelivered returned empty body', err);
      await loadData({ silent: true });
      f7?.dialog?.alert?.(
        t('box.totals.markMayHaveSucceeded'),
        t('box.totals.markDeliveredCta'),
      );
      return;
    }
    console.error('[OrderBoxTotalsPage] markDelivered failed', err);
    f7?.dialog?.alert?.(
      err?.detail || err?.message || t('common.errorTryAgain'),
      t('general.error'),
    );
  } finally {
    marking.value = false;
  }
}

// ── Live-refresh strategy ──────────────────────────────────────────────────
// Primary signal: ClubMembership.{membershipId} → .OrderBoxTotalsChanged
// (see src/realtime/echo.js for the transport). The event is id-only — never
// rendered directly, it just tells us to refetch the authoritative totals.
// A ~60s safety poll (was 30s pre-realtime) plus on-focus refetch cover the
// gap while the socket is down/backgrounded; the poll is scoped to the live
// member-visible states (SHIPPED awaiting the member's mark / auto-deliver, and
// DELIVERED awaiting the admin's settle) — see isLivePollVariant.
const membershipChannelName = computed(() => `ClubMembership.${membership.value?.id}`);
let isSubscribedToTotals = false;
let unsubscribeReconnect = null;
let pollTimer = null;

function handleOrderBoxTotalsChanged(event) {
  // Scope to the box in view — the membership channel carries events for
  // every box owned by this member, not just this page's box.
  const eventBoxId = event?.order_box_id;
  if (eventBoxId != null && Number(eventBoxId) !== Number(boxId.value)) return;
  // Don't yank the totals out from under an in-flight mark-delivered tap.
  if (marking.value) return;
  loadData({ silent: true });
}

function subscribeToTotalsUpdates() {
  if (isSubscribedToTotals) return;
  if (membership.value?.id == null) return;
  echo()
    .private(membershipChannelName.value)
    .error((error) => {
      console.warn('[realtime] ClubMembership channel auth failed:', error);
    })
    .listen('.OrderBoxTotalsChanged', handleOrderBoxTotalsChanged);
  isSubscribedToTotals = true;
}

function unsubscribeFromTotalsUpdates() {
  if (!isSubscribedToTotals) return;
  echo().leave(membershipChannelName.value);
  isSubscribedToTotals = false;
}

// Poll through both member-visible live states: SHIPPED (auto-deliver cron may
// advance the box to DELIVERED after the receipt window) and DELIVERED (the
// admin may settle → CLOSED at any time). Both need the page to refresh itself.
const isLivePollVariant = computed(() =>
  variant.value === 'shipped' || variant.value === 'confirm',
);

function startPolling() {
  stopPolling();
  pollTimer = setInterval(() => {
    if (isLivePollVariant.value && !marking.value) {
      loadData({ silent: true });
    }
  }, 60_000);
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible'
      && isLivePollVariant.value
      && !marking.value) {
    loadData({ silent: true });
  }
}

// immediate: true so a cold direct-URL load of a SHIPPED/DELIVERED box starts
// polling without waiting for a variant change (both are live states whose next
// transition — DELIVERED, then CLOSED — is driven server-side, so without
// immediate the timer would never start).
watch(variant, (next) => {
  if (next === 'shipped' || next === 'confirm') startPolling();
  else stopPolling();
}, { immediate: true });

function onPageInit() {
  // F7 lifecycle: page:init fires after DOM is in place but before transition
  // animation completes. Right place to trigger data fetches that may call
  // f7.toast / f7.dialog on failure — these are safe by now.
  document.addEventListener('visibilitychange', onVisibilityChange);
  subscribeToTotalsUpdates();
  // Recovery from a socket drop — refetch to fill any gap in missed events.
  unsubscribeReconnect = onReconnected(() => {
    if (isSubscribedToTotals) loadData({ silent: true });
  });
  loadData();
}

function onPageBeforeRemove() {
  stopPolling();
  document.removeEventListener('visibilitychange', onVisibilityChange);
  unsubscribeFromTotalsUpdates();
  unsubscribeReconnect?.();
}
</script>

<style scoped>
.totals-skeleton .skeleton-text {
    border-radius: 6px;
}

.skel-w40 { width: 40%; }
.skel-w60 { width: 60%; }
.skel-w100 { width: 100%; }
.skel-h14 { height: 14px; }
.skel-h18 { height: 18px; }
.skel-h80 { height: 80px; }
.skel-h160 { height: 160px; }
.margin-top-half { margin-top: 8px; }

.totals-error-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 16px 0;
    text-align: center;
}

.totals-banner {
    padding: 0;
    overflow: hidden;
    border-radius: var(--poolit-radius-lg);
}

.totals-update-chip-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 4px;
    margin-bottom: 4px;
}

.totals-update-chip {
    min-height: 32px;
    padding-inline: 16px;
}

.totals-open-carton-wrapper {
    margin-top: 0;
    margin-bottom: 0;
}

.totals-open-carton {
    width: 100%;
}

.totals-banner-inner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
}

.totals-banner-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.totals-banner-title {
    font-size: 15px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-text-headline);
}

.totals-banner-subtitle {
    font-size: 13px;
    color: var(--poolit-text-secondary);
}

.totals-banner-draft {
    background: linear-gradient(90deg, rgba(255, 159, 10, 0.08), rgba(255, 159, 10, 0.14));
}

.totals-banner-shipped {
    background: linear-gradient(90deg, rgba(20, 184, 166, 0.06), rgba(20, 184, 166, 0.12));
}

.totals-banner-confirm {
    background: linear-gradient(90deg, rgba(34, 197, 94, 0.08), rgba(34, 197, 94, 0.16));
}

.totals-banner-receipt {
    background: linear-gradient(90deg, rgba(148, 163, 184, 0.08), rgba(148, 163, 184, 0.16));
}

.totals-meta {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.totals-meta-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
}

.totals-meta-label {
    font-size: 13px;
    color: var(--poolit-text-tertiary);
}

.totals-meta-value {
    font-size: 14px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-text-primary);
    font-feature-settings: 'tnum' on, 'lnum' on;
}

.totals-address {
    white-space: normal;
    text-align: end;
    max-width: 60%;
    line-height: 1.4;
}

.totals-breakdown-wrapper {
    padding: 0;
}

.totals-cta-toolbar {
    --f7-toolbar-bg-color: var(--poolit-bg-primary);
    padding: 8px 12px;
}

.totals-cta {
    width: 100%;
}

.totals-cta-skeleton {
    opacity: 0.5;
}

.margin-right-half {
    margin-right: 8px;
}
</style>

<style>
/* Global (un-scoped) styles for dialog body HTML, which is injected outside
   the scoped slot tree by Framework7's dialog renderer. */
.dialog-body-confirm {
    text-align: start;
    line-height: 1.5;
}

.dialog-body-amount {
    margin-top: 8px;
    font-size: 15px;
}

.dialog-body-diff {
    padding-inline-start: 1.2em;
    margin: 8px 0;
}

.dialog-body-poolshare {
    margin-top: 8px;
    font-size: 14px;
    color: #1b8a3b;
    font-weight: 600;
}
</style>
